/**
 * RFC-214 — the canonical evidence slot as a first-class entity.
 *
 * A slot tuple (`slotKey::scopeKey`) is the unit that says WHAT evidence is
 * asked for; its citations say WHICH frameworks require it. That is orthogonal
 * to a control, which says what a requirement REPRESENTS (§5 rule 1). Until
 * RFC-214 these DTOs had no client-side existence at all — the evidence ask was
 * only ever visible as a string field on a test.
 */
import { z } from "zod/v3";
import {
  EvidenceSlotOriginSchema,
  SlotCitationOriginSchema,
} from "../generated/enums";

export type {
  EvidenceSlotLifecycle,
  EvidenceSlotOrigin,
  SlotCitationOrigin,
} from "../generated/enums";

/**
 * One requirement this evidence ask satisfies. `ref` is canonicalized by
 * `canonicalizeFrameworkRef` before storage, so it always joins to the
 * framework's published registry — never free text.
 */
export const EvidenceSlotCitationDTOSchema = z.object({
  frameworkSlug: z.string(),
  frameworkName: z.string(),
  ref: z.string(),
  /** Which producers assert this ref. A set, not a scalar — §5.2. */
  origins: z.array(SlotCitationOriginSchema),
  /** Optional Test-view classification; citations themselves remain authoritative. */
  frameworkDefId: z.string().optional(),
  inclusion: z.enum(['core', 'scope', 'extended']).optional(),
  viewNaReason: z.string().nullable().optional(),
});
export type EvidenceSlotCitationDTO = z.infer<typeof EvidenceSlotCitationDTOSchema>;

export const EvidenceSlotDefDTOSchema = z.object({
  id: z.string(),
  /** `slotKey::scopeKey`, empty string for a null scope. */
  canonicalKey: z.string(),
  slotKey: z.string(),
  scopeKey: z.string().nullable(),
  /** Human-readable evidence ask from the catalog; falls back to a title-cased
   *  slotKey for tuples the catalog does not define (policy, fallback, custom).
   *  The catalog's full `description` is deliberately NOT shipped: it is a
   *  paragraph per row across ~450 rows and nothing renders it. Add it back with
   *  the reader that needs it. */
  title: z.string(),
  // No `lifecycle`: the list is ACTIVE-only by construction, so carrying it
  // would only invite a reader to branch on a value that never varies.
  definitionOrigin: EvidenceSlotOriginSchema,
  citations: z.array(EvidenceSlotCitationDTOSchema),
  /**
   * True when the CATALOG cites this tuple for nothing at all — as opposed to
   * citing it only for a framework this workspace has not enrolled. Both leave
   * `citations` empty, and they mean opposite things: the second says "your
   * program does not require this evidence", the first says "we have not mapped
   * it yet". Measured on one customer workspace, 122 of 142 empty rows were the
   * second kind, so collapsing them would misreport 20 of our own gaps as the
   * customer's scope. §6.3.2 draws the same distinction to decide scoping.
   */
  unmappedInCatalog: z.boolean(),
  /** Controls that declare this tuple. Fan-out, not ownership — RFC-172
   *  collapses CES on the tuple, so N controls still yield one evidence ask. */
  declaringControls: z.array(
    z.object({
      id: z.string(),
      controlId: z.string(),
      /** Controls-tree code (`AUD-02`) for display; never an SC-* id. */
      displayCode: z.string().optional(),
    }),
  ),
  /** Live tests keyed to this tuple (root, latest). */
  testCount: z.number().int(),
  /**
   * What `testCount` becomes after RFC §10.3's check fan-in: the number of
   * distinct `(tuple, systemInstance)` pairs. Today the API seeder materializes
   * one CES per CHECK, so `log_management::logging_configuration` is 39 tests
   * asking one question of one system. Showing only `testCount` reads that
   * inflation as coverage; showing both makes it the defect it is.
   */
  askCount: z.number().int(),
});
export type EvidenceSlotDefDTO = z.infer<typeof EvidenceSlotDefDTOSchema>;

export const EvidenceSlotListDTOSchema = z.object({
  slots: z.array(EvidenceSlotDefDTOSchema),
  /** Framework slugs the workspace has enrolled, for the citation filter. */
  enrolledFrameworks: z.array(z.object({ slug: z.string(), name: z.string() })),
});
export type EvidenceSlotListDTO = z.infer<typeof EvidenceSlotListDTOSchema>;

// ── Drill-down: the layering RFC §12 ends at ─────────────────────────
// `SCF → CES per system → N checks → findings`. Stage 1 has all four layers in
// the database; only the top one had a reader. These types expose the rest
// READ-ONLY — no `SCF-XXX` allocation, no fan-in, no schema change. Stage 2
// renames `EvidenceSlotDef` to the SCF control and `evidenceSlotDefId` to
// `scfControlId`; the rows and ids survive, so this view survives with them.

/** One check bound to a test. Stage 1 writes these as a shadow of
 *  `executionConfig.checkId`; nothing reads them yet except this view. */
export const EvidenceSlotCheckBindingDTOSchema = z.object({
  checkId: z.string(),
  provider: z.string(),
  /** Only REQUIRED bindings can produce PASSED/FAILED (§10.3). */
  role: z.string(),
  /** Human-readable schedule, e.g. `every 1d` or a cron expression. */
  schedule: z.string().nullable(),
  /** Latest terminal run's result for this check, or null when never run. */
  lastResult: z.string().nullable(),
  lastRunAt: z.date().nullable(),
});
export type EvidenceSlotCheckBindingDTO = z.infer<
  typeof EvidenceSlotCheckBindingDTOSchema
>;

export const EvidenceSlotTestDTOSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  status: z.string(),
  /** RFC-234 M3 — how the Test's evidence is collected. */
  collection: z.enum(['automated', 'policy_link', 'attestation', 'screenshot', 'upload']).nullable(),
  /** NOT NULL since RFC-181 phase 2 — a manual test resolves to the
   *  workspace's `sys-manual-{workspaceId}` sentinel rather than to nothing. */
  systemInstanceId: z.string(),
  systemLabel: z.string().nullable(),
  bindings: z.array(EvidenceSlotCheckBindingDTOSchema),
});
export type EvidenceSlotTestDTO = z.infer<typeof EvidenceSlotTestDTOSchema>;

export const EvidenceSlotDetailDTOSchema = z.object({
  evidenceSlotDefId: z.string(),
  canonicalKey: z.string(),
  tests: z.array(EvidenceSlotTestDTOSchema),
  /**
   * Distinct systems this slot's tests cover — the post-fan-in test count.
   * `tests.length - systemCount` is the inflation §10.3 removes.
   */
  systemCount: z.number().int(),
});
export type EvidenceSlotDetailDTO = z.infer<typeof EvidenceSlotDetailDTOSchema>;
